import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  KIT_PRODUCTS,
  type CreateKitSerialPairRequest,
  type CreateKitSerialPairResponse,
  type DeleteKitSerialPairResponse,
  type KitSerialPair,
  type PaginatedKitSerialPairs,
} from '@/domain/kit-serial-pair';
import { VITE_URL_SERVICE } from '@/utils/envs';

const httpPublic = axios.create({
  baseURL: VITE_URL_SERVICE,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
};

export const useKitPairsSection = () => {
  const [selectedProductId, setSelectedProductId] = useState(KIT_PRODUCTS[0].id);
  const [principalSerial, setPrincipalSerial] = useState('');
  const [complementSerial, setComplementSerial] = useState('');
  const [filterPrincipalSerial, setFilterPrincipalSerial] = useState('');
  const [appliedFilter, setAppliedFilter] = useState('');
  const [pairs, setPairs] = useState<KitSerialPair[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [totalRows, setTotalRows] = useState(0);

  const principalInputRef = useRef<HTMLInputElement>(null);
  const complementInputRef = useRef<HTMLInputElement>(null);

  const focusPrincipalInput = () => {
    setTimeout(() => principalInputRef.current?.focus(), 50);
  };

  const fetchPairs = useCallback(
    async (page: number, pageSize: number, principalSerialFilter: string) => {
      setIsLoadingList(true);

      try {
        const { data } = await httpPublic.get<PaginatedKitSerialPairs>(
          '/api/v3/ops-izzi/kit-serial-pairs',
          {
            params: {
              page,
              pageSize,
              ...(principalSerialFilter ? { principalSerial: principalSerialFilter } : {}),
            },
          },
        );

        setPairs(data.data);
        setTotalRows(data.total);
      } catch (error) {
        toast.error(getErrorMessage(error, 'No se pudo cargar las parejas registradas.'));
        setPairs([]);
        setTotalRows(0);
      } finally {
        setIsLoadingList(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchPairs(paginationModel.page, paginationModel.pageSize, appliedFilter);
  }, [fetchPairs, paginationModel, appliedFilter]);

  const onSave = async () => {
    const trimmedPrincipal = principalSerial.trim();
    const trimmedComplement = complementSerial.trim();

    if (!trimmedPrincipal || !trimmedComplement) {
      toast.warning('Escanea la serie de caja y la serie de control.');
      return;
    }

    setIsSaving(true);

    try {
      const payload: CreateKitSerialPairRequest = {
        principalSerial: trimmedPrincipal,
        complementSerial: trimmedComplement,
      };

      const { data } = await httpPublic.post<CreateKitSerialPairResponse>(
        '/api/v3/ops-izzi/kit-serial-pairs',
        payload,
      );

      toast.success(
        `Pareja registrada: caja ${data.principalSerial} + control ${data.complementSerial}`,
      );

      setPrincipalSerial('');
      setComplementSerial('');
      focusPrincipalInput();

      if (paginationModel.page !== 0) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      } else {
        fetchPairs(0, paginationModel.pageSize, appliedFilter);
      }
    } catch (error) {
      toast.error(
        getErrorMessage(error, 'No se pudo registrar la pareja. Intenta de nuevo.'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async (id: string) => {
    const confirmed = window.confirm('¿Eliminar esta pareja de kit? Esta acción no se puede deshacer.');
    if (!confirmed) return;

    setIsLoadingList(true);

    try {
      const { data } = await httpPublic.delete<DeleteKitSerialPairResponse>(
        `/api/v3/ops-izzi/kit-serial-pairs/${id}`,
      );

      toast.success(data.message || 'Pareja de kit eliminada correctamente.');
      fetchPairs(paginationModel.page, paginationModel.pageSize, appliedFilter);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar la pareja.'));
      setIsLoadingList(false);
    }
  };

  const onChangeSelectedProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProductId(event.target.value);
  };

  const onChangePrincipalSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrincipalSerial(event.target.value);
  };

  const onChangeComplementSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComplementSerial(event.target.value);
  };

  const onKeyDownPrincipalSerial = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      complementInputRef.current?.focus();
    }
  };

  const onKeyDownComplementSerial = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSave();
    }
  };

  const onChangeFilterPrincipalSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterPrincipalSerial(event.target.value);
  };

  const onKeyDownFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSearch();
    }
  };

  const onSearch = () => {
    setAppliedFilter(filterPrincipalSerial.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onClearFilter = () => {
    setFilterPrincipalSerial('');
    setAppliedFilter('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onChangePaginationModel = (model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
  };

  const canSave = !!principalSerial.trim() && !!complementSerial.trim() && !isSaving;

  return {
    kitProducts: KIT_PRODUCTS,
    selectedProductId,
    onChangeSelectedProduct,
    principalSerial,
    onChangePrincipalSerial,
    onKeyDownPrincipalSerial,
    complementSerial,
    onChangeComplementSerial,
    onKeyDownComplementSerial,
    principalInputRef,
    complementInputRef,
    onSave,
    canSave,
    isSaving,
    isLoadingList,
    pairs,
    filterPrincipalSerial,
    onChangeFilterPrincipalSerial,
    onKeyDownFilter,
    onSearch,
    onClearFilter,
    appliedFilter,
    paginationModel,
    onChangePaginationModel,
    totalRows,
    onDelete,
  };
};
